/**
 * useOfflineData Hook
 * Synchronisiert Daten mit IndexedDB für Offline-Zugriff
 */

import { useState, useEffect, useCallback } from 'react';
import offlineStorage from '../services/offlineStorage';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export function useOfflineData() {
  const [syncStatus, setSyncStatus] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Sync-Status laden
  const loadSyncStatus = useCallback(async () => {
    try {
      const status = await offlineStorage.getSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error('[OfflineData] Fehler beim Laden des Sync-Status:', error);
    }
  }, []);

  useEffect(() => {
    loadSyncStatus();
  }, [loadSyncStatus]);

  // Klienten synchronisieren
  const syncKlienten = useCallback(async () => {
    if (!navigator.onLine) {
      console.log('[OfflineData] Offline - Klienten werden nicht synchronisiert');
      return false;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/betreuung/klienten`);
      if (!response.ok) throw new Error('Klienten konnten nicht geladen werden');
      
      const data = await response.json();
      const klienten = data.klienten || data || [];
      
      await offlineStorage.saveKlienten(klienten);
      console.log(`[OfflineData] ${klienten.length} Klienten synchronisiert`);
      return true;
    } catch (error) {
      console.error('[OfflineData] Fehler beim Synchronisieren der Klienten:', error);
      setLastError(error.message);
      return false;
    }
  }, []);

  // Aufgaben synchronisieren
  const syncAufgaben = useCallback(async () => {
    if (!navigator.onLine) {
      return false;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/betreuung/aufgaben`);
      if (!response.ok) throw new Error('Aufgaben konnten nicht geladen werden');
      
      const data = await response.json();
      const aufgaben = data.aufgaben || data || [];
      
      await offlineStorage.saveAufgaben(aufgaben);
      console.log(`[OfflineData] ${aufgaben.length} Aufgaben synchronisiert`);
      return true;
    } catch (error) {
      console.error('[OfflineData] Fehler beim Synchronisieren der Aufgaben:', error);
      setLastError(error.message);
      return false;
    }
  }, []);

  // Fristen synchronisieren
  const syncFristen = useCallback(async () => {
    if (!navigator.onLine) {
      return false;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/fristen`);
      if (!response.ok) throw new Error('Fristen konnten nicht geladen werden');
      
      const data = await response.json();
      const fristen = data.fristen || data || [];
      
      await offlineStorage.saveFristen(fristen);
      console.log(`[OfflineData] ${fristen.length} Fristen synchronisiert`);
      return true;
    } catch (error) {
      console.error('[OfflineData] Fehler beim Synchronisieren der Fristen:', error);
      setLastError(error.message);
      return false;
    }
  }, []);

  // Alle Daten synchronisieren
  const syncAll = useCallback(async () => {
    setIsSyncing(true);
    setLastError(null);

    try {
      await Promise.all([
        syncKlienten(),
        syncAufgaben(),
        syncFristen()
      ]);

      await loadSyncStatus();
      console.log('[OfflineData] Synchronisation abgeschlossen');
    } catch (error) {
      console.error('[OfflineData] Synchronisation fehlgeschlagen:', error);
      setLastError(error.message);
    } finally {
      setIsSyncing(false);
    }
  }, [syncKlienten, syncAufgaben, syncFristen, loadSyncStatus]);

  // Offline-Klienten abrufen
  const getOfflineKlienten = useCallback(async () => {
    try {
      return await offlineStorage.getKlienten();
    } catch (error) {
      console.error('[OfflineData] Fehler beim Laden der Offline-Klienten:', error);
      return [];
    }
  }, []);

  // Offline-Klient abrufen
  const getOfflineKlient = useCallback(async (id) => {
    try {
      const klient = await offlineStorage.getKlient(id);
      if (klient) {
        await offlineStorage.markKlientAsViewed(id);
      }
      return klient;
    } catch (error) {
      console.error('[OfflineData] Fehler beim Laden des Offline-Klienten:', error);
      return null;
    }
  }, []);

  // Zuletzt angesehene Klienten
  const getRecentKlienten = useCallback(async (limit = 10) => {
    try {
      return await offlineStorage.getRecentlyViewedKlienten(limit);
    } catch (error) {
      console.error('[OfflineData] Fehler beim Laden der zuletzt angesehenen Klienten:', error);
      return [];
    }
  }, []);

  // Offline-Aufgaben abrufen
  const getOfflineAufgaben = useCallback(async () => {
    try {
      return await offlineStorage.getAufgaben();
    } catch (error) {
      console.error('[OfflineData] Fehler beim Laden der Offline-Aufgaben:', error);
      return [];
    }
  }, []);

  // Offline-Fristen abrufen
  const getOfflineFristen = useCallback(async () => {
    try {
      return await offlineStorage.getFristen();
    } catch (error) {
      console.error('[OfflineData] Fehler beim Laden der Offline-Fristen:', error);
      return [];
    }
  }, []);

  // Cache leeren
  const clearOfflineData = useCallback(async () => {
    try {
      await offlineStorage.clearAllData();
      await loadSyncStatus();
      console.log('[OfflineData] Offline-Daten gelöscht');
    } catch (error) {
      console.error('[OfflineData] Fehler beim Löschen der Offline-Daten:', error);
    }
  }, [loadSyncStatus]);

  return {
    syncStatus,
    isSyncing,
    lastError,
    syncAll,
    syncKlienten,
    syncAufgaben,
    syncFristen,
    getOfflineKlienten,
    getOfflineKlient,
    getRecentKlienten,
    getOfflineAufgaben,
    getOfflineFristen,
    clearOfflineData
  };
}

export default useOfflineData;
